import { Analytics, ArrowBack, ArrowForward, Assessment, CheckCircle, People, TrendingUp } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { createCorporateLoginUrl, createLoginUrl } from '@src/App/Utils/authUrlGenerator';
import { EIdentityProvider } from '@src/globals';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * "NotAllowed" function.
 *
 * @returns {JSX.Element} - The JSX element.
 */
function NotAllowed() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).notloggedin;
    const navigate = useNavigate();
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    useEffect(() => {
        if (location.pathname.startsWith('/loggedout')) return;
        localStorage.setItem('redirect', location.pathname + location.hash);
    }, []);

    const features = [
        { icon: <Assessment />, title: 'Skill Assessment', description: 'Umfassende Kompetenz-Bewertung' },
        { icon: <TrendingUp />, title: 'Progress Tracking', description: 'Entwicklung über Zeit verfolgen' },
        { icon: <People />, title: 'Team Management', description: 'Team verwalten und entwickeln' },
        { icon: <Analytics />, title: 'Analytics Dashboard', description: 'Datenbasierte Einblicke und Reports' },
    ];

    const benefits = [
        'Berufliche Kompetenzen verfolgen und entwickeln',
        'Personalisierte Lernempfehlungen erhalten',
        'Team-Performance und Wachstum überwachen',
        'Umfassende Skill-Reports generieren',
        'KI-gestützte Karriere-Beratung nutzen',
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    minHeight: '100vh',
                }}
            >
                {/* Left Side - Marketing Content */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        px: { xs: 2, md: 4, lg: 6 },
                        py: 4,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box sx={{ maxWidth: 500 }}>
                        {/* Back Link */}
                        <Typography
                            component="a"
                            href="https://t4lo.de"
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'none',
                                fontSize: '0.75rem',
                                mb: 2,
                                display: 'block',
                                '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            ← t4lo.de
                        </Typography>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                fontWeight: 400,
                                lineHeight: 1.2,
                                color: 'text.primary',
                                mb: 3,
                            }}
                        >
                            Entfalte das volle{' '}
                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                                Potenzial Deines Teams
                            </Box>
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                mb: 3,
                                lineHeight: 1.5,
                            }}
                        >
                            T4lo revolutioniert die Art, wie Teams ihre Kompetenzen entwickeln und verwalten. Mit
                            KI-gestützten Insights und personalisierten Lernpfaden bringst Du Dein Team auf das nächste
                            Level.
                        </Typography>

                        {/* Benefits List */}
                        <Box sx={{ mb: 4 }}>
                            {benefits.map((benefit, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            bgcolor: '#00D5A9',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <CheckCircle sx={{ fontSize: 12, color: 'white' }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                        {benefit}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Feature Cards */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 2,
                                mt: 4,
                            }}
                        >
                            {features.map((feature, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        bgcolor:
                                            theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.15)
                                                : alpha(theme.palette.primary.main, 0.05),
                                        p: 2,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: 'primary.main',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mr: 1.5,
                                            }}
                                        >
                                            {React.cloneElement(feature.icon, { sx: { fontSize: 16, color: 'white' } })}
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}
                                            >
                                                {feature.title}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Right Side - Login Form */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        px: { xs: 2, md: 4 },
                        py: 4,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box sx={{ maxWidth: 384, mx: 'auto', width: '100%', mt: { xs: 4, lg: 4 } }}>
                        <Card
                            sx={{
                                border: '1px solid',
                                borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.background.paper, 0.8)
                                        : alpha(theme.palette.background.paper, 0.9),
                            }}
                        >
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Chip
                                    label={langStrings.title}
                                    sx={{
                                        bgcolor: alpha('#00D5A9', 0.1),
                                        color: '#00D5A9',
                                        border: 'none',
                                        mb: 2,
                                        fontSize: '0.75rem',
                                    }}
                                />

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        mb: 3,
                                        lineHeight: 1.5,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {langStrings.text}
                                </Typography>

                                {/* Login Buttons */}
                                <Box sx={{ mb: 3 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        endIcon={<ArrowForward />}
                                        href={createCorporateLoginUrl(
                                            window.location.hostname,
                                            EIdentityProvider.TALENTINO,
                                        )}
                                        sx={{
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                                color: 'primary.contrastText',
                                            },
                                        }}
                                    >
                                        Corporate ID Login
                                    </Button>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        fullWidth
                                        href={createLoginUrl(window.location.hostname)}
                                        sx={{
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Standard Login
                                    </Button>
                                </Box>

                                {/* Divider */}
                                <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                                    <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'grey.300' }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            px: 2,
                                            color: 'grey.500',
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        oder
                                    </Typography>
                                    <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'grey.300' }} />
                                </Box>

                                {/* Social Login Buttons */}
                                <Box sx={{ mb: 3 }}>
                                    <Button
                                        variant="white"
                                        fullWidth
                                        href={createLoginUrl(window.location.hostname)}
                                        startIcon={
                                            <svg width="16" height="16" viewBox="0 0 24 24">
                                                <path fill="#00BCF2" d="M0 0h11.377v11.372H0z" />
                                                <path fill="#00BCF2" d="M12.623 0H24v11.372H12.623z" />
                                                <path fill="#00BCF2" d="M0 12.623h11.377V24H0z" />
                                                <path fill="#00BCF2" d="M12.623 12.623H24V24H12.623z" />
                                            </svg>
                                        }
                                        sx={{
                                            py: 1.2,
                                            mb: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Mit Microsoft anmelden
                                    </Button>

                                    <Button
                                        variant="primary"
                                        fullWidth
                                        href={createCorporateLoginUrl(
                                            window.location.hostname,
                                            EIdentityProvider.GOOGLE,
                                        )}
                                        startIcon={
                                            <svg width="16" height="16" viewBox="0 0 24 24">
                                                <path
                                                    fill="currentColor"
                                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                                />
                                            </svg>
                                        }
                                        sx={{
                                            py: 1.2,
                                            mb: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Mit Google anmelden
                                    </Button>

                                    <Button
                                        variant="primary"
                                        fullWidth
                                        href={createCorporateLoginUrl(
                                            window.location.hostname,
                                            EIdentityProvider.FACEBOOK,
                                        )}
                                        startIcon={
                                            <svg width="16" height="16" viewBox="0 0 24 24">
                                                <path
                                                    fill="currentColor"
                                                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                                                />
                                            </svg>
                                        }
                                        sx={{
                                            py: 1.2,
                                            mb: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Mit Facebook anmelden
                                    </Button>

                                    <Button
                                        variant="primary"
                                        fullWidth
                                        href={createCorporateLoginUrl(
                                            window.location.hostname,
                                            EIdentityProvider.APPLE,
                                        )}
                                        startIcon={
                                            <svg width="16" height="16" viewBox="0 0 24 24">
                                                <path
                                                    fill="currentColor"
                                                    d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
                                                />
                                            </svg>
                                        }
                                        sx={{
                                            py: 1.2,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Mit Apple ID anmelden
                                    </Button>
                                </Box>

                                {/* Final CTA Button */}
                                <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                                    <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'grey.300' }} />
                                    <Typography
                                        variant="caption"
                                        component="button"
                                        onClick={() => navigate(`/${ELinks.COMPANY_REGISTRATION}`)}
                                        sx={{
                                            px: 2,
                                            color: 'primary.main',
                                            textTransform: 'uppercase',
                                            fontSize: '0.75rem',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            '&:hover': {
                                                color: 'primary.dark',
                                            },
                                        }}
                                    >
                                        Jetzt registrieren!
                                    </Typography>
                                    <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'grey.300' }} />
                                </Box>

                                <Button
                                    variant="gradient"
                                    fullWidth
                                    href={createLoginUrl(window.location.hostname)}
                                    sx={{
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    Starte deine T4lo Experience heute!
                                </Button>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default NotAllowed;
